import { httpClient } from "./client";
import type { LeagueApi } from "./interfaces";
import type {
  LeagueCreate,
  LeagueUpdate,
  LeagueResponse,
  LeagueSeasonCreate,
  LeagueSeasonUpdate,
  LeagueSeasonResponse,
  LeagueDivisionCreate,
  LeagueDivisionResponse,
  LeagueRegistrationResponse,
  LeagueSessionCreate,
  LeagueSessionResponse,
  LeagueSessionRegistrationResponse,
  LeagueMatchCreate,
  LeagueMatchResultReport,
  LeagueMatchResponse,
  LeagueStandingResponse,
  UUID,
} from "../types";
import type {
  MyLeagueRegistrationPageResponse,
  LeagueDiscoveryPageResponse,
  LeaguePublicDivisionResponse,
  LeaguePublicSessionPageResponse,
  LeagueSessionParticipationResponse,
} from "../types/generated";

function publicQuery(params: object = {}): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) query.set(key, String(value));
  }
  return query.size ? `?${query}` : "";
}

export const leagueApi: LeagueApi = {
  getMySessionParticipation: (sessionIds) => {
    const query = new URLSearchParams();
    for (const id of sessionIds) query.append("session_ids", id);
    return httpClient.get<LeagueSessionParticipationResponse[]>(
      `/leagues/my-session-participation?${query}`,
    );
  },
  discover: (params = {}) =>
    httpClient.get<LeagueDiscoveryPageResponse>(
      `/leagues/discover${publicQuery(params)}`,
    ),
  getPublicDivision: (divisionId) =>
    httpClient.get<LeaguePublicDivisionResponse>(
      `/leagues/public/divisions/${encodeURIComponent(divisionId)}`,
    ),
  listPublicSessions: (seasonId, params = {}) =>
    httpClient.get<LeaguePublicSessionPageResponse>(
      `/leagues/public/seasons/${encodeURIComponent(seasonId)}/sessions${publicQuery(params)}`,
    ),
  create: (facilityId: UUID, data: LeagueCreate) =>
    httpClient.post<LeagueResponse>(`/leagues/facilities/${facilityId}`, data),

  list: (facilityId: UUID, status?: string) => {
    const qs = status ? `?status=${status}` : "";
    return httpClient.get<LeagueResponse[]>(
      `/leagues/facilities/${facilityId}${qs}`,
    );
  },

  // Player-facing alias of `list` (GET /leagues/facilities/{facility_id}?status=)
  // for the discovery browse (S3b): the same anonymous-readable facility-leagues
  // read, named for the player journey. Reuses the identical path + the optional
  // status filter — NOT a second endpoint.
  listLeaguesByFacility: (facilityId: UUID, status?: string) => {
    const qs = status ? `?status=${status}` : "";
    return httpClient.get<LeagueResponse[]>(
      `/leagues/facilities/${facilityId}${qs}`,
    );
  },

  get: (leagueId: UUID) =>
    httpClient.get<LeagueResponse>(`/leagues/${leagueId}`),

  update: (leagueId: UUID, data: LeagueUpdate) =>
    httpClient.patch<LeagueResponse>(`/leagues/${leagueId}`, data),

  // Seasons
  createSeason: (leagueId: UUID, data: LeagueSeasonCreate) =>
    httpClient.post<LeagueSeasonResponse>(`/leagues/${leagueId}/seasons`, data),

  listSeasons: (leagueId: UUID) =>
    httpClient.get<LeagueSeasonResponse[]>(`/leagues/${leagueId}/seasons`),

  getSeason: (seasonId: UUID) =>
    httpClient.get<LeagueSeasonResponse>(`/leagues/seasons/${seasonId}`),

  updateSeason: (seasonId: UUID, data: LeagueSeasonUpdate) =>
    httpClient.patch<LeagueSeasonResponse>(
      `/leagues/seasons/${seasonId}`,
      data,
    ),

  // Divisions
  createDivision: (seasonId: UUID, data: LeagueDivisionCreate) =>
    httpClient.post<LeagueDivisionResponse>(
      `/leagues/seasons/${seasonId}/divisions`,
      data,
    ),

  listDivisions: (seasonId: UUID) =>
    httpClient.get<LeagueDivisionResponse[]>(
      `/leagues/seasons/${seasonId}/divisions`,
    ),

  // Registration
  registerPlayer: (divisionId: UUID, playerId: string) =>
    httpClient.post<LeagueRegistrationResponse>(
      `/leagues/divisions/${divisionId}/registrations`,
      {
        player_id: playerId,
      },
    ),

  // The player SELF-register into a division (POST
  // /leagues/divisions/{division_id}/registrations → LeagueRegistrationResponse).
  // The SAME endpoint as registerPlayer, but the player-journey form (S3b): the
  // backend resolves the caller from the token and IGNORES any body player_id
  // (router forces player_id = caller), so NO player_id is sent — a player can
  // never register someone else. The backend owns ALL eligibility/capacity/lifecycle
  // checks; the error surfaces as a STATUS CODE (409 full/duplicate, 403|400
  // ineligible/not-open, 404 not-found) the caller maps to honest copy.
  registerForDivision: (divisionId: UUID) =>
    httpClient.post<LeagueRegistrationResponse>(
      `/leagues/divisions/${divisionId}/registrations`,
      {},
    ),

  withdrawRegistration: (registrationId: UUID) =>
    httpClient.delete<void>(`/leagues/registrations/${registrationId}`),

  listRegistrations: (divisionId: UUID) =>
    httpClient.get<LeagueRegistrationResponse[]>(
      `/leagues/divisions/${divisionId}/registrations`,
    ),

  // Sessions
  createSession: (seasonId: UUID, data: LeagueSessionCreate) =>
    httpClient.post<LeagueSessionResponse>(
      `/leagues/seasons/${seasonId}/sessions`,
      data,
    ),

  listSessions: (seasonId: UUID) =>
    httpClient.get<LeagueSessionResponse[]>(
      `/leagues/seasons/${seasonId}/sessions`,
    ),

  // Flex session registration
  registerForSession: (sessionId: UUID) =>
    httpClient.post<LeagueSessionRegistrationResponse>(
      `/leagues/sessions/${sessionId}/registrations`,
      {},
    ),

  cancelSessionRegistration: (sessionRegId: UUID) =>
    httpClient.delete<void>(`/leagues/session-registrations/${sessionRegId}`),

  listSessionRegistrations: (sessionId: UUID) =>
    httpClient.get<LeagueSessionRegistrationResponse[]>(
      `/leagues/sessions/${sessionId}/registrations`,
    ),

  // Matches
  createMatch: (sessionId: UUID, data: LeagueMatchCreate) =>
    httpClient.post<LeagueMatchResponse>(
      `/leagues/sessions/${sessionId}/matches`,
      data,
    ),

  reportMatchResult: (matchId: UUID, data: LeagueMatchResultReport) =>
    httpClient.post<LeagueMatchResponse>(
      `/leagues/matches/${matchId}/result`,
      data,
    ),

  listSessionMatches: (sessionId: UUID) =>
    httpClient.get<LeagueMatchResponse[]>(
      `/leagues/sessions/${sessionId}/matches`,
    ),

  // Standings
  getStandings: (divisionId: UUID) =>
    httpClient.get<LeagueStandingResponse[]>(
      `/leagues/divisions/${divisionId}/standings`,
    ),

  // The caller's OWN league registrations across EVERY facility (GET
  // /users/me/league-registrations → MyLeagueRegistrationPageResponse), SERVER-SCOPED
  // to the auth user and the league analogue of tournamentApi.getMyRegistrations.
  // Accepts paging/division filters, never a caller id. The path is the
  // canonical /users/me/... form that resolves directly (no reliance on the BFF's
  // /me/ → /users/me/ rewrite quirk — same convention as complianceApi). Backs the
  // player "My leagues" history list.
  listMyLeagueRegistrations: (params = {}) =>
    httpClient.get<MyLeagueRegistrationPageResponse>(
      `/users/me/league-registrations${publicQuery(params)}`,
    ),
};
